import { appManager } from "../../../../../AppManager";
import { get, writable } from "svelte/store";
import { MatrixAssignmentRowManager } from "./assignments/MatrixAssignmentRowManager";
import { MatrixTools } from "../../../../../lib/matrices/MatrixTools";
import { MatrixManager } from "./MatrixManager";
import { MatrixMapManager } from "./MatrixMapManager";
/**
 * Contains all trips for a single mode for a collection of matrices.
 */
export class MatrixRowManager {
    constructor(parent, data) {
        this.parent = parent;
        this.data = data;
        this.tripsScenarioId = parent.getData().id;
        this.id = writable(this.data.id);
        this.visible = writable(this.data.visible);
        this.active = writable(false);
        this.matrix = new MatrixManager(this, this.tripsScenarioId, this.data);
        this.assigments = writable([]);
        const assigments = [];
        this.data.assignments.forEach(x => {
            const pl = new MatrixAssignmentRowManager(this, this.tripsScenarioId, x);
            assigments.push(pl);
        });
        this.assigments.set(assigments);
        this.locationsMapManager = new MatrixMapManager(this, this.matrix);
        this.visible.subscribe(v => {
            if (this.data.visible == v)
                return;
            this.data.visible = v;
            this.save().then();
        });
    }
    addToMap(map) {
        this.map = map;
        this.locationsMapManager.addToMap(map);
    }
    async onEditMatrixClick() {
        if (typeof this.map === "undefined")
            return;
        const view = this.map.getView();
        const matrix = this.matrix.getData();
        const clonedMatrix = MatrixTools.cloneMatrix(matrix);
        const result = await appManager.ModalHooks.OdEditModal.open({
            matrix: clonedMatrix,
            center: [view.center.lng, view.center.lat],
            zoom: view.zoom
        });
        if (result === "cancelled")
            return;
        clonedMatrix.id = matrix.id;
        await this.updateMatrix(clonedMatrix);
    }
    async save() {
        const token = await appManager.api.getAccessToken();
        const result = await appManager.api.projects.updateMatrix({
            tripScenarioId: this.tripsScenarioId,
            id: this.data.id,
            data: {
                visible: this.data.visible,
            },
            accessToken: token
        });
    }
    /**
     * Gets the count for a given od-pair and a profile.
     *
     * @param profile The profile.
     * @param movementKey The key (an od-pair).
     */
    getCount(profile, movementKey) {
        const movement = this.matrix.getMovement(movementKey);
        if (typeof movement === "undefined")
            return 0;
        let count = 0;
        get(this.assigments).forEach(pl => {
            const visible = get(pl.getIsVisible());
            if (!visible)
                return;
            if (profile != get(pl.profile))
                return;
            count += movement.count;
        });
        return count;
    }
    /**
     * The available routing profiles.
     *
     * @returns The profiles.
     */
    getAvailableProfiles() {
        return this.parent.getAvailableProfiles();
    }
    /**
     * Gets the profile layers.
     *
     * @returns The profile layers.
     */
    getMatrixAssignmentRows() {
        return this.assigments;
    }
    /**
     * Adds a new assignment layer.
     *
     * @param data The details of the new assignment layer.
     */
    async addAssignment(data) {
        const token = await appManager.api.getAccessToken();
        const id = await appManager.api.projects.createMatrixAssignment({
            tripScenarioId: this.tripsScenarioId,
            matrixId: this.data.id,
            data: data,
            accessToken: token
        });
        const assignment = {
            id: id,
            profile: data.profile,
            color: data.color,
            visible: data.visible,
        };
        const assigments = get(this.assigments);
        const newProfileLayer = new MatrixAssignmentRowManager(this, this.tripsScenarioId, assignment);
        assigments.push(newProfileLayer);
        this.assigments.set(assigments);
    }
    /**
     * Deletes an assignment layer.
     *
     * @param id The id.
     */
    async deleteAssignment(id) {
        const token = await appManager.api.getAccessToken();
        await appManager.api.projects.deleteMatrixAssignment({
            tripScenarioId: this.tripsScenarioId,
            id: id,
            data: {},
            accessToken: token
        });
        const assigments = get(this.assigments);
        assigments.splice(assigments.findIndex(x => get(x.id) == id), 1);
        this.assigments.set(assigments);
    }
    refreshTrips() {
        this.parent.refreshTrips();
    }
    /**
     * Deletes this matrix.
     */
    async delete() {
        const assigments = get(this.assigments);
        this.data.assignments.forEach(pl => {
            assigments.splice(assigments.findIndex(x => get(x.id) == pl.id), 1);
        });
        this.assigments.set(assigments);
        await this.parent.deleteMatrix(this.data.id);
        this.locationsMapManager.remove();
    }
    /**
     * Copies this matrix and it's layers.
     */
    async copy() {
        const data = this.matrix.getData();
        const od = {
            name: `${data.name} (copy)`,
            properties: data.properties,
            locations: data.locations.map(v => {
                return {
                    latitude: v.latitude,
                    longitude: v.longitude,
                    properties: v.properties,
                };
            }),
            elements: data.elements.map(v => {
                return {
                    count: v.count,
                    fromIdx: v.fromIdx,
                    toIdx: v.toIdx
                };
            }),
        };
        const assigments = get(this.assigments).map(x => {
            const colorAndVisualization = get(x.color);
            return {
                profile: get(x.profile),
                visible: get(x.visible),
                color: colorAndVisualization.color,
                visualizationType: colorAndVisualization.visualizationType
            };
        });
        await this.parent.addMatrix(od, assigments);
    }
    /**
     * Updates the matrix.
     */
    async updateMatrix(updatedMatrix) {
        const token = await appManager.api.getAccessToken();
        // persist the matrix first.
        const savedMatrix = await appManager.api.projects.updateMatrix({
            tripScenarioId: this.tripsScenarioId,
            id: updatedMatrix.id,
            data: updatedMatrix,
            accessToken: token,
        });
        updatedMatrix.locations = savedMatrix.locations;
        updatedMatrix.elements = savedMatrix.elements;
        // update data.
        this.matrix.updateData(savedMatrix);
        this.parent.refreshTrips();
    }
    /**
     * Copies this matrix and it's layers but transposes it.
     */
    async copyAndTranspose() {
        const data = this.matrix.getData();
        const od = {
            name: `${data.name} (copy)`,
            properties: data.properties,
            locations: data.locations.map(v => {
                return {
                    latitude: v.latitude,
                    longitude: v.longitude,
                    properties: v.properties,
                };
            }),
            elements: data.elements.map(v => {
                return {
                    count: v.count,
                    fromIdx: v.toIdx,
                    toIdx: v.fromIdx
                };
            }),
        };
        const assigments = get(this.assigments).map(x => {
            const colorAndVisualization = get(x.color);
            return {
                profile: get(x.profile),
                visible: get(x.visible),
                color: colorAndVisualization.color,
                visualizationType: colorAndVisualization.visualizationType
            };
        });
        await this.parent.addMatrix(od, assigments);
    }
}
